import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Device } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, ConfirmButton, EmptyState, FilterBar, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, DeviceList, batteryText, keys, needsAttention, sortValue } from './api';
import { DevicesLive } from './Live';
import { SharedLinkBadge } from '../shared-reception/parts';
import { PairCard } from './PairCard';

const PAGE_SIZE = 20;

export function StateBadge({ online }: { online: boolean }) {
  return <Badge tone={online ? 'good' : 'bad'}>{online ? t('devicesUi.state.online') : t('devicesUi.state.offline')}</Badge>;
}

function RenameCell({ device }: { device: Device }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(device.name);
  const rename = useMutation({
    mutationFn: () => api.patch(`${BASE}/${device.id}`, { body: { name }, schema: Device }),
    onSuccess: async () => {
      setEditing(false);
      await qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
  if (!editing) {
    return (
      <Button
        onClick={() => {
          setName(device.name);
          setEditing(true);
        }}
        aria-label={`${t('devicesUi.rename')} ${device.name}`}
      >
        {t('devicesUi.rename')}
      </Button>
    );
  }
  return (
    <form
      className="inline-flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        rename.mutate();
      }}
    >
      <input
        aria-label={t('devicesUi.rename.label', { name: device.name })}
        className="min-h-9 w-40 rounded-md border border-slate-300 px-2 text-sm"
        maxLength={60}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Button type="submit" variant="primary" disabled={rename.isPending || name.trim() === ''}>
        {t('common.save')}
      </Button>
      <Button onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
    </form>
  );
}

export function DevicesTab() {
  const { sites, siteId } = useSite();
  const canManage = useCan('sites.manage_devices');
  const qc = useQueryClient();
  const toast = useToast();
  const list = useListState({ sort: 'name' });
  const [siteFilter, setSiteFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<'all' | 'online' | 'offline' | 'attention'>('all');
  const [pairing, setPairing] = useState(false);

  const q = useQuery({
    queryKey: keys.list(siteFilter || 'all'),
    queryFn: () => api.get(BASE, { query: { siteId: siteFilter || undefined }, schema: DeviceList }),
    placeholderData: keepPreviousData,
  });
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? '';
  const unpair = useMutation({
    mutationFn: (d: Device) => api.delete(`${BASE}/${d.id}`),
    onSuccess: async (_r, d) => {
      toast(t('devicesUi.unpair.done', { name: d.name }));
      await qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const rows = useMemo(() => {
    const term = list.search.trim().toLowerCase();
    const items = (q.data ?? []).filter((d) => {
      if (term && !`${d.name} ${d.model ?? ''}`.toLowerCase().includes(term)) return false;
      if (stateFilter === 'online') return d.online;
      if (stateFilter === 'offline') return !d.online;
      if (stateFilter === 'attention') return needsAttention(d);
      return true;
    });
    const dir = list.order === 'asc' ? 1 : -1;
    const name = (id: string) => sites.find((s) => s.id === id)?.name ?? '';
    return [...items].sort((a, b) => {
      const av = list.sort === 'site' ? name(a.siteId) : sortValue(a, list.sort ?? 'name');
      const bv = list.sort === 'site' ? name(b.siteId) : sortValue(b, list.sort ?? 'name');
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  }, [q.data, list.search, list.sort, list.order, stateFilter, sites]);

  const pageRows = rows.slice((list.page - 1) * PAGE_SIZE, list.page * PAGE_SIZE);
  const pairButton = canManage ? (
    <Button variant="primary" onClick={() => setPairing(true)} disabled={pairing}>
      {t('devicesUi.pair.button')}
    </Button>
  ) : null;
  const liveSites = siteFilter ? [siteFilter] : sites.length ? sites.map((s) => s.id) : siteId ? [siteId] : [];
  const cols = canManage ? 7 : 6;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <DevicesLive siteIds={liveSites} />
        {pairButton}
      </div>
      {pairing ? <PairCard onDone={() => setPairing(false)} /> : null}
      <FilterBar columns="sm:grid-cols-3">
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('devicesUi.filter.site')}
          value={siteFilter}
          onChange={(e) => {
            setSiteFilter(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('devicesUi.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('devicesUi.filter.state')}
          value={stateFilter}
          onChange={(e) => {
            setStateFilter(e.target.value as typeof stateFilter);
            list.resetPage();
          }}
        >
          <option value="all">{t('devicesUi.filter.stateAll')}</option>
          <option value="online">{t('devicesUi.filter.stateOnline')}</option>
          <option value="offline">{t('devicesUi.filter.stateOffline')}</option>
          <option value="attention">{t('devicesUi.filter.stateAttention')}</option>
        </SelectField>
      </FilterBar>
      <ErrorLine error={unpair.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('devicesUi.tab.devices')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('devicesUi.col.name')} field="name" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <SortHeader label={t('devicesUi.col.site')} field="site" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <SortHeader label={t('devicesUi.col.state')} field="state" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <SortHeader label={t('devicesUi.col.battery')} field="battery" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <SortHeader label={t('devicesUi.col.version')} field="version" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <SortHeader label={t('devicesUi.col.lastSeen')} field="lastSeen" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                {canManage ? <Th>{t('devicesUi.col.actions')}</Th> : null}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={cols}>
                    <EmptyState message={t('devicesUi.empty')} action={pairButton} />
                  </td>
                </tr>
              ) : null}
              {pageRows.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link className="font-medium text-brand hover:underline" to={`/manage/devices/${d.id}`}>
                      {d.name}
                    </Link>
                    {d.model ? <div className="text-xs text-slate-600">{d.model}</div> : null}
                    <SharedLinkBadge device={d} />
                  </td>
                  <td className="px-4 py-3">{siteName(d.siteId)}</td>
                  <td className="px-4 py-3">
                    <StateBadge online={d.online} />
                    {d.openAlerts.length > 0 ? (
                      <div className="mt-1 text-xs text-amber-900">
                        {d.openAlerts.length === 1 ? t('devicesUi.alerts.one') : t('devicesUi.alerts.count', { count: d.openAlerts.length })}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">{batteryText(d)}</td>
                  <td className="px-4 py-3">
                    {d.appVersion ? t('devicesUi.version.value', { app: d.appVersion, bundle: d.bundleVersion ?? d.appVersion }) : ''}
                    {d.updateAvailable ? (
                      <div className="mt-1">
                        <Badge tone="warn">{t('devicesUi.version.updateAvailable')}</Badge>
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">{d.lastSeenAt ? formatDateTime(d.lastSeenAt) : t('devicesUi.neverSeen')}</td>
                  {canManage ? (
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {d.shared ? null : <RenameCell device={d} />}
                        <ConfirmButton
                          label={`${t('devicesUi.unpair')} ${d.name}`}
                          confirmLabel={d.shared ? t('srt.devices.unpairConfirm', { name: d.name }) : t('devicesUi.unpair.confirm', { name: d.name })}
                          pending={unpair.isPending}
                          onConfirm={() => unpair.mutate(d)}
                        />
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={rows.length} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
